import React, { useEffect, useRef, useState } from "react";
import { coordsForToken, projectPolar, TOTAL, formatCoords } from "../lib/coords";
import { Search, Maximize2 } from "lucide-react";

const RARITY_COLOR = {
    ultra_legendary: "#F472B6",
    legendary: "#F59E0B",
    mythic: "#8bfaa3",
    epic: "#38BDF8",
};

// Real flat polar-projection satellite image of Antarctica (NASA Blue Marble derivative).
const BACKGROUND_IMAGES = [
    "https://upload.wikimedia.org/wikipedia/commons/thumb/9/9c/Antarctica_satellite_orthographic.jpg/1200px-Antarctica_satellite_orthographic.jpg",
];

/**
 * AntarcticaPolarMap
 * Real Antarctica flat (polar azimuthal) projection. The background uses a
 * real satellite orthographic image of the continent. Tokens (sample) are
 * dotted using deterministic golden-spiral mapping. Top tokens are coloured
 * by rarity and clickable.
 */
export default function AntarcticaPolarMap({
    topTokens = [],
    soldNumbers = [],
    onPickToken,
    sampleSize = 5000,
    height = 560,
}) {
    const wrapperRef = useRef(null);
    const canvasRef = useRef(null);
    const [hover, setHover] = useState(null);
    const [size, setSize] = useState(560);
    const [searchN, setSearchN] = useState("");
    const [imgLoaded, setImgLoaded] = useState(false);
    const imgRef = useRef(null);

    useEffect(() => {
        const onResize = () => {
            if (wrapperRef.current) {
                const w = wrapperRef.current.clientWidth;
                setSize(Math.min(w, height));
            }
        };
        onResize();
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, [height]);

    // Pre-load background image
    useEffect(() => {
        const img = new Image();
        img.onload = () => {
            imgRef.current = img;
            setImgLoaded(true);
        };
        img.onerror = () => setImgLoaded(false);
        img.src = BACKGROUND_IMAGES[0];
    }, []);

    // Render
    useEffect(() => {
        const c = canvasRef.current;
        if (!c) return;
        const dpr = window.devicePixelRatio || 1;
        c.width = size * dpr;
        c.height = size * dpr;
        c.style.width = `${size}px`;
        c.style.height = `${size}px`;
        const ctx = c.getContext("2d");
        ctx.scale(dpr, dpr);
        ctx.clearRect(0, 0, size, size);

        const cx = size / 2;
        const cy = size / 2;
        const r = size / 2 - 4;

        // Outer disk background (deep navy)
        ctx.fillStyle = "#020617";
        ctx.fillRect(0, 0, size, size);

        // Clip to circle
        ctx.save();
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.closePath();
        ctx.clip();

        // Background satellite image (orthographic Antarctica)
        if (imgLoaded && imgRef.current) {
            try {
                ctx.drawImage(imgRef.current, cx - r, cy - r, r * 2, r * 2);
                // Slight dark overlay for contrast
                ctx.fillStyle = "rgba(2, 6, 23, 0.25)";
                ctx.fillRect(cx - r, cy - r, r * 2, r * 2);
            } catch (e) {
                console.warn("Map background draw failed:", e);
            }
        } else {
            // Fallback gradient
            const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
            grad.addColorStop(0, "rgba(56, 189, 248, 0.12)");
            grad.addColorStop(1, "rgba(2, 6, 23, 1)");
            ctx.fillStyle = grad;
            ctx.fillRect(cx - r, cy - r, r * 2, r * 2);
        }

        ctx.restore();

        // Outer ring
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.strokeStyle = "rgba(56, 189, 248, 0.3)";
        ctx.lineWidth = 1;
        ctx.stroke();

        // Latitude rings
        ctx.strokeStyle = "rgba(255,255,255,0.07)";
        for (let i = 1; i <= 4; i++) {
            ctx.beginPath();
            ctx.arc(cx, cy, (r * i) / 4, 0, Math.PI * 2);
            ctx.stroke();
        }
        // Longitude lines
        for (let a = 0; a < 12; a++) {
            const t = (a / 12) * Math.PI * 2;
            ctx.beginPath();
            ctx.moveTo(cx, cy);
            ctx.lineTo(cx + Math.sin(t) * r, cy - Math.cos(t) * r);
            ctx.stroke();
        }

        // Token dots (sample stratified)
        const step = Math.max(1, Math.floor(TOTAL / sampleSize));
        const sold = new Set(soldNumbers);
        const topNums = new Set(topTokens.map((t) => t.number));
        for (let n = 1; n <= TOTAL; n += step) {
            if (topNums.has(n)) continue;
            const { lat, lon } = coordsForToken(n);
            const { x, y } = projectPolar(lat, lon, size);
            if (sold.has(n)) {
                ctx.fillStyle = "rgba(245, 158, 11, 0.95)";
                ctx.beginPath();
                ctx.arc(x, y, 1.6, 0, Math.PI * 2);
                ctx.fill();
            } else {
                ctx.fillStyle = "rgba(56, 189, 248, 0.6)";
                ctx.fillRect(x, y, 1.2, 1.2);
            }
        }

        // Top tokens (big colour-coded markers)
        topTokens.forEach((t) => {
            const lat = t.lat;
            const lon = t.lon;
            if (lat == null || lon == null) return;
            const { x, y } = projectPolar(lat, lon, size);
            const color = RARITY_COLOR[t.rarity] || "#F59E0B";
            // Outer halo
            ctx.beginPath();
            ctx.arc(x, y, 14, 0, Math.PI * 2);
            ctx.fillStyle = color + "22";
            ctx.fill();
            ctx.beginPath();
            ctx.arc(x, y, 8, 0, Math.PI * 2);
            ctx.fillStyle = color + "55";
            ctx.fill();
            ctx.beginPath();
            ctx.arc(x, y, 4.5, 0, Math.PI * 2);
            ctx.fillStyle = color;
            ctx.fill();
            ctx.lineWidth = 1.5;
            ctx.strokeStyle = "rgba(255,255,255,0.9)";
            ctx.stroke();
        });

        // Pole marker
        ctx.beginPath();
        ctx.arc(cx, cy, 2.5, 0, Math.PI * 2);
        ctx.fillStyle = "#38BDF8";
        ctx.fill();
        ctx.font = "10px JetBrains Mono";
        ctx.fillStyle = "#38BDF8";
        ctx.fillText("90°S", cx + 5, cy + 3);
    }, [size, soldNumbers, topTokens, sampleSize, imgLoaded]);

    const onMove = (e) => {
        const rect = canvasRef.current.getBoundingClientRect();
        const mx = e.clientX - rect.left;
        const my = e.clientY - rect.top;
        const cx = size / 2;
        const cy = size / 2;
        const dx = mx - cx;
        const dy = cy - my;
        const r = Math.sqrt(dx * dx + dy * dy);
        const radius = size / 2 - 4;
        if (r > radius) {
            setHover(null);
            return;
        }
        const lat = -90 + (r / (size / 2)) * 30;
        const theta = Math.atan2(dx, dy);
        const lon = ((theta * 180) / Math.PI + 540) % 360 - 180;
        // Detect nearest top token
        let near = null;
        for (const t of topTokens) {
            if (t.lat == null) continue;
            const p = projectPolar(t.lat, t.lon, size);
            const d = Math.hypot(p.x - mx, p.y - my);
            if (d < 16) {
                near = t;
                break;
            }
        }
        setHover({ lat, lon, x: mx, y: my, near });
    };

    const onClick = () => {
        if (!hover || !onPickToken) return;
        if (hover.near) {
            onPickToken(hover.near.number);
            return;
        }
        // nearest token
        let best = 1, bestD = Infinity;
        for (let n = 1; n <= TOTAL; n += 5) {
            const c = coordsForToken(n);
            const d = (c.lat - hover.lat) ** 2 + (c.lon - hover.lon) ** 2;
            if (d < bestD) { bestD = d; best = n; }
        }
        onPickToken(best);
    };

    const submitSearch = (e) => {
        e.preventDefault();
        const n = parseInt(searchN, 10);
        if (n >= 1 && n <= TOTAL && onPickToken) onPickToken(n);
    };

    return (
        <div ref={wrapperRef} className="relative w-full" data-testid="antarctica-polar-map">
            <div className="relative" style={{ height: size }}>
                <canvas
                    ref={canvasRef}
                    onMouseMove={onMove}
                    onMouseLeave={() => setHover(null)}
                    onClick={onClick}
                    className="cursor-crosshair mx-auto block"
                />
                <div className="absolute top-3 left-3 glass px-3 py-2 text-xs font-mono pointer-events-none">
                    <div className="text-slate-500 uppercase tracking-[0.2em] text-[10px]">
                        Map · Antarctica
                    </div>
                    <div className="text-frost">14,200 parcels · 1,000 km² each</div>
                </div>
                <div className="absolute bottom-3 right-3 glass px-3 py-2 text-xs font-mono space-y-1 pointer-events-none">
                    <Legend color={RARITY_COLOR.ultra_legendary} label="Ultra Legendary" />
                    <Legend color={RARITY_COLOR.legendary} label="Legendary" />
                    <Legend color={RARITY_COLOR.mythic} label="Mythic" />
                    <Legend color={RARITY_COLOR.epic} label="Epic" />
                    <Legend color="#F59E0B" label="Sold" small />
                    <Legend color="#38BDF8" label="Available" small />
                </div>
                {hover && (
                    <div
                        className="absolute pointer-events-none glass px-2 py-1 text-[11px] font-mono"
                        style={{ left: hover.x + 12, top: hover.y + 12 }}
                    >
                        {hover.near ? (
                            <>
                                <div className="text-aurora font-serif text-base">{hover.near.title}</div>
                                <div className="text-slate-400 text-[10px] uppercase tracking-widest">
                                    {(hover.near.rarity || "").replace("_", " ")}
                                </div>
                                <div className="text-frost mt-1">
                                    {formatCoords(hover.near.lat, hover.near.lon)}
                                </div>
                            </>
                        ) : (
                            <div className="text-frost">{formatCoords(hover.lat, hover.lon)}</div>
                        )}
                    </div>
                )}
            </div>

            <form
                onSubmit={submitSearch}
                className="mt-4 flex items-center gap-2 max-w-md mx-auto"
                data-testid="map-search-form"
            >
                <div className="relative flex-1">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                    <input
                        data-testid="map-search-input"
                        value={searchN}
                        onChange={(e) => setSearchN(e.target.value)}
                        placeholder="Find token by number (1 – 14,200)"
                        className="w-full bg-slate-900 border border-white/10 pl-10 pr-3 py-2 text-sm font-mono focus:border-frost focus:outline-none"
                    />
                </div>
                <button
                    data-testid="map-search-submit"
                    type="submit"
                    className="btn-sharp border border-white/20 px-4 py-2 text-xs hover:border-frost hover:text-frost flex items-center gap-1"
                >
                    <Maximize2 className="w-3 h-3" /> Locate
                </button>
            </form>
        </div>
    );
}

function Legend({ color, label, small }) {
    return (
        <div className="flex items-center gap-2">
            <span
                className="inline-block rounded-full"
                style={{
                    width: small ? 6 : 9,
                    height: small ? 6 : 9,
                    background: color,
                    boxShadow: `0 0 ${small ? 4 : 7}px ${color}`,
                }}
            />
            <span className={small ? "text-slate-400" : "text-white"}>{label}</span>
        </div>
    );
}
